import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AdminSection } from "./admin";

type Submission = {
  id: string;
  inquiry_type: string;
  name: string;
  email: string;
  message: string;
  budget: string | null;
  timeline: string | null;
  status: "new" | "in_progress" | "responded" | "archived" | "spam";
  forwarded_at: string | null;
  created_at: string;
};

const STATUSES: Submission["status"][] = ["new", "in_progress", "responded", "archived", "spam"];

export const Route = createFileRoute("/_authenticated/admin/contact")({
  head: () => ({ meta: [{ title: "Admin · Contact · Buddha KA" }, { name: "robots", content: "noindex" }] }),
  component: ContactInbox,
});

function ContactInbox() {
  const [rows, setRows] = useState<Submission[] | null>(null);
  const [error, setError] = useState<string>("");
  const [filter, setFilter] = useState<Submission["status"] | "all">("new");

  async function load() {
    setError("");
    const query = supabase
      .from("contact_submissions")
      .select("id, inquiry_type, name, email, message, budget, timeline, status, forwarded_at, created_at")
      .order("created_at", { ascending: false })
      .limit(200);
    const { data, error } = filter === "all"
      ? await query
      : await query.eq("status", filter);
    if (error) { setError(error.message); return; }
    setRows((data ?? []) as Submission[]);
  }

  useEffect(() => { load(); }, [filter]);

  async function updateStatus(id: string, status: Submission["status"]) {
    const { error } = await supabase.from("contact_submissions").update({ status }).eq("id", id);
    if (error) { setError(error.message); return; }
    load();
  }

  return (
    <AdminSection title="Contact inbox" description="Every transmission received through the contact form.">
      <div className="flex flex-wrap items-center gap-2 pb-6">
        {(["all", ...STATUSES] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-full px-3 py-1 text-xs uppercase tracking-[0.2em] ${
              filter === s ? "bg-gold-gradient text-obsidian font-semibold" : "border hairline text-muted-foreground hover:text-foreground"
            }`}
          >
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-red-300">{error}</p>}
      {rows === null && <p className="text-sm text-muted-foreground">Loading…</p>}
      {rows && rows.length === 0 && <p className="text-sm text-muted-foreground">No submissions in this bucket.</p>}

      <ul className="space-y-3">
        {rows?.map((r) => (
          <li key={r.id} className="rounded-xl border hairline bg-charcoal p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <p className="font-serif text-lg text-gold-luminous">{r.name} <span className="text-xs text-muted-foreground">· {r.inquiry_type}</span></p>
                <a href={`mailto:${r.email}`} className="text-xs text-muted-foreground underline underline-offset-4">{r.email}</a>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={r.status}
                  onChange={(e) => updateStatus(r.id, e.target.value as Submission["status"])}
                  className="rounded-full border hairline bg-obsidian px-3 py-1 text-xs uppercase tracking-[0.2em]"
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
              </div>
            </div>
            {(r.budget || r.timeline) && (
              <p className="mt-2 text-xs text-muted-foreground">
                {r.budget && <>Budget: {r.budget} </>}
                {r.timeline && <>· Timeline: {r.timeline}</>}
              </p>
            )}
            <p className="mt-3 whitespace-pre-wrap text-sm text-foreground">{r.message}</p>
            <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              {r.forwarded_at ? `Forwarded ${new Date(r.forwarded_at).toLocaleString()}` : "Awaiting email forwarding (email domain pending)"}
            </p>
          </li>
        ))}
      </ul>
    </AdminSection>
  );
}
